import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import type { Guid, ITicketInfo } from "@kiraah-shared/common";
import { SearchFilterBase } from "@kiraah-shared/common";
import { TranslateModule } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import { MobileTicketApiClient } from "../services/mobile-ticket-api.client";
import { mobileEntityStatusDotColor } from "../../mission-task/helpers/mobile-mission-task-display";
import { mobileTicketRecipientLabel, mobileTicketRequesterLabel, mobileTicketTypeBadgeClass, mobileTicketTypeDisplayLabel, mobileTicketTypeMaterialIcon } from "../helpers/mobile-ticket-demand-display";

@Component({
  selector: "mobile-demand-list-page",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./mobile-demand-list-page.component.html",
  styleUrl: "./mobile-demand-list-page.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileDemandListPageComponent implements OnInit {
  private readonly api = inject(MobileTicketApiClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly moduleAccess = inject(MobileModuleAccessService);

  readonly ticketAccess = this.moduleAccess.eachatTicketAccess;

  readonly tickets = signal<ITicketInfo[]>([]);
  readonly listStatus = signal<"idle" | "loading" | "error" | "success">("idle");

  readonly mobileTicketTypeBadgeClass = mobileTicketTypeBadgeClass;
  readonly mobileTicketTypeMaterialIcon = mobileTicketTypeMaterialIcon;
  readonly mobileTicketTypeDisplayLabel = mobileTicketTypeDisplayLabel;
  readonly mobileTicketRequesterLabel = mobileTicketRequesterLabel;
  readonly mobileTicketRecipientLabel = mobileTicketRecipientLabel;
  readonly mobileEntityStatusDotColor = mobileEntityStatusDotColor;

  ngOnInit(): void {
    void this.reload();
  }

  async reload(): Promise<void> {
    this.listStatus.set("loading");
    try {
      const rows = await firstValueFrom(this.api.filterTickets(this.buildListFilter()));
      this.tickets.set(rows);
      this.listStatus.set("success");
    } catch {
      this.listStatus.set("error");
    }
  }

  trackTicket(index: number, t: ITicketInfo): string {
    return t.Uid != null ? String(t.Uid) : `t-${index}`;
  }

  openDemandDetail(uid: Guid | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!this.ticketAccess().hasReadAccess) {
      return;
    }
    if (uid == null || uid === "") {
      return;
    }
    void this.router.navigate([String(uid)], { relativeTo: this.route });
  }

  private buildListFilter(): SearchFilterBase {
    /** Comme la grille web : pas de `LvfAppModules` (sinon brouillons sans TaskEntity TICKET exclus). */
    return new SearchFilterBase({
      Paginate: true,
      PageSize: 60,
      Page: 1,
      WithIdentities: true,
      // E-Achat mobile (eachat-mobile) et Spiral web (spiral-web) partagent les tickets ;
      // le filtre catégorie×app tenant (commit scope TaskCategory) utilise X-APP-UID :
      // sans ce flag, la liste mobile est vide alors que le web remonte les mêmes demandes.
      IgnoreTenantAppScope: true,
    });
  }
}
